<template>
  <!-- Estilo unificado con SelectLayersModal y UpdateStatusesModal:
       rounded-lg + border + shadow, HEADER Meta con título + close button (SVG)
       y FOOTER con v-button. Se abre desde LayoutBuilder al clickear una
       polilínea con ancho > 0 en viewerMode='progress'. -->
  <b-modal :active.sync="isModalActive" :width="500" scroll="keep" :can-cancel="['escape','outside']">
    <div class="rounded-lg bg-white overflow-hidden border border-[#E4E6EB] shadow-lg">

      <!-- HEADER -->
      <header class="flex justify-between items-center px-4 py-3 border-b border-[#E4E6EB] bg-white">
        <div class="flex-1 min-w-0">
          <h2 class="text-base font-semibold text-gray-900 break-words whitespace-normal">
            {{ $t('linear_advance') }}
          </h2>
        </div>
        <button
          @click="isModalActive = false"
          class="flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-full text-gray-500 hover:bg-gray-100 transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-gray-300 focus:ring-offset-1"
          aria-label="Cerrar">
          <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
            <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd"></path>
          </svg>
        </button>
      </header>

      <!-- BODY -->
      <section class="px-4 py-4 space-y-4 bg-white">
        <div class="bg-gray-50 border border-[#E4E6EB] rounded px-3 py-2 text-sm">
          <span class="text-gray-500 uppercase text-xs font-semibold">{{ $t('total_length') }}:</span>
          <strong class="ml-2 text-gray-900">{{ totalLength.toFixed(2) }} m</strong>
        </div>

        <!-- Selector visual de dirección — mini-SVG con la forma real de la
             polilínea, dos extremos clickeables y chevrons indicando el sentido
             del avance. Si directionLocked=true (ya hay avance previo) los
             extremos no responden a click y solo sirven de referencia visual. -->
        <div v-if="projectedPoints.length >= 2">
          <label class="flex items-center gap-1 text-xs uppercase text-gray-500 font-semibold mb-2">
            {{ $t('advance_start') }}
            <b-icon v-if="directionLocked" pack="fas" icon="lock" size="is-small" />
          </label>
          <div class="advance-svg-wrap">
            <svg :width="svgWidth" :height="svgHeight" class="advance-svg">
              <!-- Polilínea real escalada al viewport -->
              <polyline
                :points="pointsString"
                fill="none"
                stroke="#9ca3af"
                stroke-width="2.5"
                stroke-linejoin="round"
                stroke-linecap="round"
              />

              <!-- Chevrons distribuidos a lo largo del path, orientados según
                   la dirección elegida. Comunican el sentido del avance aunque
                   la polilínea sea zigzagueante. -->
              <polygon
                v-for="(arrow, i) in arrowsForDirection"
                :key="'arr-' + i"
                :points="_GetArrowPoints(arrow.cx, arrow.cy, arrow.angle)"
                class="advance-arrow"
              />

              <!-- Extremo A (vertex[0]) -->
              <g
                @click="_OnEndpointClick('l2r')"
                :class="['endpoint-grp', { 'endpoint-grp--locked': directionLocked }]"
              >
                <circle
                  :cx="projectedPoints[0].x"
                  :cy="projectedPoints[0].y"
                  r="11"
                  :class="['endpoint', { 'endpoint--selected': direction === 'l2r' }]"
                />
                <text
                  :x="projectedPoints[0].x"
                  :y="projectedPoints[0].y + 4"
                  text-anchor="middle"
                  class="endpoint-label">A</text>
              </g>

              <!-- Extremo B (vertex[last]) -->
              <g
                @click="_OnEndpointClick('r2l')"
                :class="['endpoint-grp', { 'endpoint-grp--locked': directionLocked }]"
              >
                <circle
                  :cx="projectedPoints[projectedPoints.length - 1].x"
                  :cy="projectedPoints[projectedPoints.length - 1].y"
                  r="11"
                  :class="['endpoint', { 'endpoint--selected': direction === 'r2l' }]"
                />
                <text
                  :x="projectedPoints[projectedPoints.length - 1].x"
                  :y="projectedPoints[projectedPoints.length - 1].y + 4"
                  text-anchor="middle"
                  class="endpoint-label">B</text>
              </g>
            </svg>
          </div>
          <p class="text-xs text-gray-500 mt-1">
            {{ directionLocked ? $t('advance_start_locked_hint', [_StartLetter]) : $t('advance_start_hint', [_StartLetter]) }}
          </p>
        </div>

        <!-- ESTADO primero — primero defino "qué" hago, luego "cuánto".
             Patrón consistente con UpdateStatusesModal del viewer-pixi. -->
        <b-field :label="$t('status')">
          <multiselect
            v-model="selectedStatus"
            :options="statusesForSelect"
            track-by="value"
            label="label"
            :placeholder="$t('select_status')"
            :show-labels="false"
            :allow-empty="true"
            :max-height="200"
            open-direction="top"
            class="advance-status-select"
          >
            <template slot="option" slot-scope="{ option }">
              <div class="flex items-center gap-2">
                <span class="status-swatch" :style="{ backgroundColor: option.color }"></span>
                <span>{{ option.label }}</span>
              </div>
            </template>
            <template slot="singleLabel" slot-scope="{ option }">
              <div class="flex items-center gap-2">
                <span class="status-swatch" :style="{ backgroundColor: option.color }"></span>
                <span>{{ option.label }}</span>
              </div>
            </template>
            <template slot="noOptions">{{ $t('no_options_available') }}</template>
          </multiselect>
        </b-field>

        <!-- Aviso del modo consecutivo: explica por qué algunos estados
             pueden estar deshabilitados. Solo en strict/capped. -->
        <p v-if="orderMode !== 'free'" class="text-xs text-gray-500 -mt-3 flex items-center gap-1">
          <b-icon pack="fas" icon="circle-info" size="is-small" />
          {{ orderMode === 'strict' ? $t('consecutive_hint_strict') : $t('consecutive_hint_capped') }}
        </p>

        <b-field :label="$t('executed_meters')">
          <!-- Campo texto con validación numérica (dígitos + 1 punto + 2
               decimales) en vez de b-numberinput: los controles +/- con step
               0.01 generaban artefactos de coma flotante en los decimales. -->
          <b-input
            v-model="metersInput"
            inputmode="decimal"
            placeholder="0.00"
            expanded
            @input="_OnMetersInput"
            @blur="_OnMetersBlur"
          />
        </b-field>
        <p v-if="selectedStatus" class="text-xs text-gray-500 -mt-3 mb-2">
          {{ $t('available_for_status', [selectedStatus.label, remainingLength.toFixed(2)]) }}
        </p>

        <!-- Fecha del registro — equivalente al `start_date` del flow legacy
             mecánico. Por default es hoy; el usuario puede cambiarla para
             registrar un avance hecho en una fecha anterior. -->
        <b-field :label="$t('registration_date')">
          <b-datepicker
            v-model="startDate"
            :max-date="todayDate"
            position="is-top-right"
            icon="calendar-day"
            icon-pack="fas"
            editable
          />
        </b-field>

        <div class="execution-summary" v-if="totalLength > 0 && (hasRejects || statusBreakdown.length > 0)">
          <div class="execution-summary__row">
            <span class="execution-summary__label">{{ (hasRejects ? $t('current_progress') : $t('progress_by_status')) }}:</span>
            <span class="text-xs text-gray-500">/ {{ totalLength.toFixed(2) }} m</span>
          </div>

          <!-- Modelo de bolsas (capa con rechazos): flujo parcial. Mostramos la
               distribución vigente del segmento: por-iniciar (gris), cada etapa con
               sus metros (su color) y rechazado (color de rechazo, re-ejecutable). -->
          <div v-if="hasRejects" class="status-breakdown">
            <template v-if="poolBreakdown.length">
              <div v-for="brk in poolBreakdown" :key="'pool-' + brk.key" class="status-breakdown__row">
                <span class="status-swatch" :style="{ backgroundColor: brk.color }"></span>
                <span class="status-breakdown__label" :title="brk.label">{{ brk.label || '—' }}</span>
                <div class="status-breakdown__bar">
                  <div class="status-breakdown__bar-fill"
                       :style="{ width: _PoolPercent(brk.meters) + '%', backgroundColor: brk.color }"></div>
                </div>
                <span class="status-breakdown__meters">{{ brk.meters.toFixed(2) }} m</span>
              </div>
            </template>
            <div v-else class="status-breakdown__empty text-xs text-gray-500 py-1">{{ $t('no_advance_registered') }}</div>
          </div>

          <!-- Clásico (zanjas): cada estado es un proceso paralelo (cavar, tapar,
               fibra, etc.) que arranca desde 0 y avanza independiente del resto. Lo
               mostramos como una mini-barra por estado, donde el ancho refleja el %
               cubierto sobre el total de la polilínea. El estado que el usuario está
               editando se refuerza con el preview del nuevo delta (rayas diagonales). -->
          <div v-else class="status-breakdown">
            <div v-for="brk in statusBreakdown" :key="'brk-' + brk.value"
                 :class="['status-breakdown__row', { 'status-breakdown__row--editing': _IsEditingStatus(brk.value) }]">
              <span class="status-swatch" :style="{ backgroundColor: brk.color }"></span>
              <span class="status-breakdown__label" :title="brk.label">{{ brk.label || '—' }}</span>
              <div class="status-breakdown__bar">
                <!-- Parte ya registrada (existente, sólida). -->
                <div class="status-breakdown__bar-fill"
                     :style="{
                       width: _ExistingPercentOfStatus(brk.value) + '%',
                       backgroundColor: brk.color,
                     }"></div>
                <!-- Preview del nuevo delta sobre el status editado
                     (rayas diagonales para distinguirlo de lo ya registrado). -->
                <div v-if="_IsEditingStatus(brk.value) && meters > 0"
                     class="status-breakdown__bar-preview"
                     :style="{
                       left: _ExistingPercentOfStatus(brk.value) + '%',
                       width: _PreviewPercentOfStatus(brk.value) + '%',
                       backgroundColor: brk.color,
                     }"></div>
              </div>
              <span class="status-breakdown__meters">{{ brk.meters.toFixed(2) }} m</span>
            </div>
          </div>
        </div>
      </section>

      <!-- Banner de error inline cuando el backend rechaza el apply. -->
      <div v-if="errorMessage" class="bg-red-50 border-t border-red-200 px-4 py-2 text-red-700 text-sm">
        {{ errorMessage }}
      </div>

      <!-- FOOTER -->
      <footer class="px-4 py-3 border-t border-[#E4E6EB] bg-white">
        <div class="w-full flex items-center justify-between gap-2">
          <v-button color="grey" icon="door-closed" size="small"
                    :disabled="isApplying"
                    @click="isModalActive = false">
            {{ $t('cancel') }}
          </v-button>
          <v-button icon="check" size="small"
                    :loading="isApplying"
                    :disabled="isApplying"
                    @click="_OnApply">
            {{ $t('apply') }}
          </v-button>
        </div>
      </footer>
    </div>
  </b-modal>
</template>

<script>
import Multiselect from 'vue-multiselect'

const SVG_WIDTH  = 440
const SVG_HEIGHT = 130
const SVG_PAD    = 18    // padding interno del viewport para que los circles
                         // de los extremos no salgan del SVG
const ARROW_COUNT = 3    // cantidad de chevrons distribuidos a lo largo del path
const ARROW_SIZE  = 7    // tamaño base del chevron (radio en SVG units)

// Flag de comportamiento del RECHAZO en la máquina de estados. HARDCODEADO por
// ahora; TODO: moverlo al administrador de pictogramas (metadata de capa/pictograma)
// y pasarlo como prop, igual que `orderMode`. Debe mantenerse en sync con la
// constante homónima de PictogramService (backend). Valores:
//   'reset'     → al inicio: cualquier rechazo vuelve a "sin ejecutar".
//   'step_back' → un paso atrás: vuelve a ofrecer el paso de decisión aceptar/
//                 rechazar ANTERIOR (conserva el avance previo).
// Lo usan statusEligibility y currentFlowState (mantener AMBOS coherentes).
const REJECT_RETURN_MODE = 'step_back'

export default {
  name: 'RegisterAdvanceModal',

  components: { Multiselect },

  props: {},

  data() {
    return {
      isModalActive: false,
      // Flag que se enciende mientras el padre persiste el avance en el
      // backend. Mientras dure, el botón Aplicar muestra loading y el modal
      // no se cierra. El padre llama `hide()` si éxito o `setError(msg)`
      // si falla.
      isApplying: false,
      errorMessage: '',
      // Fecha del avance (Date object). Default hoy. Se manda al backend
      // como YYYY-MM-DD en _OnApply.
      startDate: new Date(),
      // El padre pasa los valores iniciales vía show(opts). La copia mutable
      // vive acá adentro — cancelar descarta sin afectar el padre. Solo se
      // propaga al hacer Apply (vía epc-apply).
      totalLength: 0,
      direction:   'l2r',
      // `meters` ahora significa "metros nuevos en este registro" (delta),
      // no el total acumulado. Cada Apply crea un nuevo segment con este
      // valor y el status elegido — el avance previo no se modifica.
      meters:      0,
      // Espejo de texto de `meters` para el campo (validación numérica manual).
      metersInput: '0',
      selectedStatus: null,
      // Lista de status disponibles para la capa actual.
      // Formato uniforme: [{ value, label, color }]. El padre la calcula
      // en _GetStatusesForLayer (statuses reales de la capa o hardcoded).
      statuses:    [],
      // Segmentos ya registrados previamente (no se editan desde acá).
      // Cada uno: { meters, status, statusLabel, statusColor }.
      previousSegments: [],
      // Vertices crudos de la polilínea ([{x,y}, ...]) — el padre los pasa
      // para que dibujemos el mini-SVG y los extremos clickeables.
      vertices:    [],
      // Si true, la dirección ya está fijada por un avance previo y los
      // endpoints quedan como referencia visual (no responden a click).
      directionLocked: false,

      // Modo de orden de estados (free | strict | capped). Define qué estados
      // son elegibles y el tope del input. 'free' = comportamiento histórico.
      orderMode: 'free',

      svgWidth:  SVG_WIDTH,
      svgHeight: SVG_HEIGHT,
    }
  },

  computed: {
    /**
     * Vertices proyectados al viewport SVG (escala uniforme + Y flippeado).
     * Centrados en el viewport sin estirar (preserva la forma real).
     */
    projectedPoints() {
      const verts = this.vertices
      if (!verts || verts.length < 2) return []

      let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity
      for (const v of verts) {
        if (v.x < minX) minX = v.x
        if (v.x > maxX) maxX = v.x
        if (v.y < minY) minY = v.y
        if (v.y > maxY) maxY = v.y
      }

      // Span mínimo para evitar divisiones por cero en polilíneas planas
      // (todas las x iguales o todas las y iguales).
      const w = Math.max(maxX - minX, 1e-9)
      const h = Math.max(maxY - minY, 1e-9)

      const innerW = SVG_WIDTH  - 2 * SVG_PAD
      const innerH = SVG_HEIGHT - 2 * SVG_PAD
      const scale  = Math.min(innerW / w, innerH / h)

      // Centramos en el viewport: si la polilínea es muy alargada, sobra
      // espacio en el eje corto.
      const drawW = w * scale
      const drawH = h * scale
      const offX  = SVG_PAD + (innerW - drawW) / 2
      const offY  = SVG_PAD + (innerH - drawH) / 2

      // Y se flippea porque SVG va Y↓ y el DXF va Y↑.
      return verts.map(v => ({
        x: offX + (v.x - minX) * scale,
        y: offY + (maxY - v.y) * scale,
      }))
    },

    pointsString() {
      return this.projectedPoints.map(p => `${p.x.toFixed(2)},${p.y.toFixed(2)}`).join(' ')
    },

    /**
     * Lista de chevrons a renderizar — ubicados en posiciones interpoladas
     * (25%, 50%, 75% del path), orientados según la dirección elegida.
     *
     * Si direction='r2l' invertimos la secuencia de vertices, así los
     * chevrons apuntan del extremo B hacia el A (sentido visual del avance).
     */
    arrowsForDirection() {
      const pts = this.projectedPoints
      if (pts.length < 2) return []

      const sequence = this.direction === 'l2r' ? pts : pts.slice().reverse()

      // Acumulado de longitudes para muestrear a posiciones interpoladas.
      const cumLen = [0]
      for (let i = 1; i < sequence.length; i++) {
        const dx = sequence[i].x - sequence[i - 1].x
        const dy = sequence[i].y - sequence[i - 1].y
        cumLen.push(cumLen[i - 1] + Math.hypot(dx, dy))
      }
      const totalLen = cumLen[cumLen.length - 1]
      if (totalLen < 1e-6) return []

      const arrows = []
      for (let n = 1; n <= ARROW_COUNT; n++) {
        const targetLen = totalLen * (n / (ARROW_COUNT + 1))

        // Buscar el segmento que contiene targetLen.
        let segIdx = 1
        while (segIdx < cumLen.length && cumLen[segIdx] < targetLen) segIdx++
        if (segIdx >= cumLen.length) break

        const segStart = cumLen[segIdx - 1]
        const segEnd   = cumLen[segIdx]
        const segSpan  = segEnd - segStart
        const t = segSpan > 1e-9 ? (targetLen - segStart) / segSpan : 0

        const a = sequence[segIdx - 1]
        const b = sequence[segIdx]
        const cx = a.x + (b.x - a.x) * t
        const cy = a.y + (b.y - a.y) * t
        // Ángulo en SVG coords (Y↓), no en mundo (Y↑). Como ya usamos los
        // proyectados, el ángulo sale natural para el render.
        const angle = Math.atan2(b.y - a.y, b.x - a.x)

        arrows.push({ cx, cy, angle })
      }
      return arrows
    },

    /** Letra del extremo de inicio según direction — para mostrar en el hint. */
    _StartLetter() {
      return this.direction === 'l2r' ? 'A' : 'B'
    },

    /**
     * Metros ya registrados PARA EL STATUS SELECCIONADO. Es lo que define
     * cuánto le falta a ese proceso para llegar al final de la polilínea.
     * Cada estado avanza independiente (cavar, tapar, fibra... cada uno
     * empieza desde 0 y llega hasta totalLength).
     */
    existingMetersOfSelectedStatus() {
      if (!this.selectedStatus) return 0
      return this._ExistingMetersOfStatus(this.selectedStatus.value)
    },

    /**
     * Elegibilidad por estado (Map value → { eligible, cap }).
     *
     * DOS comportamientos según la capa:
     *  - CON rechazos (flujo aceptar/rechazar, ej. eléctrico): MÁQUINA DE ESTADOS.
     *    La polilínea está en UN estado a la vez = último positivo alcanzado
     *    procesando los segmentos en orden CRONOLÓGICO. Un rechazo (100%) RESETEA
     *    al inicio ("sin ejecutar" → re-ejecutar). Elegibles = el SIGUIENTE positivo
     *    (aceptación) + su(s) rechazo(s) emparejado(s). Tras el último positivo:
     *    proceso terminado (nada). Cada acción registra el total (sin parciales).
     *  - SIN rechazos (progresión normal): modelo clásico free/strict/capped.
     */
    statusEligibility() {
      const L = this.totalLength
      const mode = this.orderMode || 'free'
      const eps = 1e-6
      const map = new Map()
      const ex = (v) => this._ExistingMetersOfStatus(v)
      // Modo de retroceso del rechazo (constante de módulo; ver arriba).

      // Un estado es RECHAZO si su peso es < 0.
      const isReject = (s) => Number(s.weight) < 0

      // Predecesor = POSITIVO anterior en la secuencia (salta rechazos).
      let lastPositiveIdx = -1
      const prevIdx = []
      for (let i = 0; i < this.statuses.length; i++) {
        prevIdx[i] = lastPositiveIdx
        if (!isReject(this.statuses[i])) lastPositiveIdx = i
      }

      const hasRejects = this.statuses.some(isReject)

      if (!hasRejects) {
        // ---- CLÁSICO (sin rechazos): free / strict / capped ----
        for (let i = 0; i < this.statuses.length; i++) {
          const existing = ex(this.statuses[i].value)
          const p = prevIdx[i]
          let cap, eligible
          if (p < 0 || mode === 'free') {
            cap = Math.max(0, L - existing); eligible = cap > eps
          } else {
            const prevExisting = ex(this.statuses[p].value)
            if (mode === 'strict') {
              const prevDone = L > 0 ? prevExisting + eps >= L : true
              cap = prevDone ? Math.max(0, L - existing) : 0
              eligible = prevDone && cap > eps
            } else { // capped
              cap = Math.max(0, prevExisting - existing); eligible = cap > eps
            }
          }
          map.set(this.statuses[i].value, { eligible, cap })
        }
        return map
      }

      // ---- MODELO DE BOLSAS (con rechazos): flujo PARCIAL con retorno del rechazo ----
      // Cada estado positivo tiene una "bolsa" = metros que llegaron a esa etapa y no
      // avanzaron ni fueron rechazados (ver _SimulatePools). Reglas:
      //  - Ejecutar (nivel 0): tope = por-iniciar + rechazado (los rechazados vuelven
      //    a estar disponibles para re-ejecutar). Elegible mientras haya de eso.
      //  - Aceptar/Rechazar del nivel k (k≥1): consumen de la bolsa del nivel anterior
      //    (bags[k-1]) → tope = bags[k-1]. Son PARCIALES (no resetean).
      //  - TODOS los niveles con metros pendientes están abiertos a la vez, cada uno con
      //    SU propio tope (su bolsa); Ejecutar disponible si hay pool ejecutable.
      const pools = this.pools
      const posLevelOfIdx = pools.posLevelOfIdx
      const bags = pools.bags
      const executable = (pools.unstarted || 0) + (pools.rejected || 0)

      for (let i = 0; i < this.statuses.length; i++) {
        let eligible = false
        let cap = 0
        if (!isReject(this.statuses[i])) {
          const pk = posLevelOfIdx[i]
          if (pk === 0) {
            cap = executable
            eligible = cap > eps
          } else {
            cap = bags[pk - 1] || 0
            eligible = cap > eps
          }
        } else {
          // Rechazo: emparejado con su aceptación (prevIdx = índice del accept);
          // consume de la bolsa del nivel anterior a ESE accept.
          const acceptIdx = prevIdx[i]
          const kAccept = (acceptIdx >= 0) ? posLevelOfIdx[acceptIdx] : -1
          if (kAccept >= 1) {
            cap = bags[kAccept - 1] || 0
            eligible = cap > eps
          }
        }
        map.set(this.statuses[i].value, { eligible, cap })
      }
      return map
    },

    /**
     * Opciones del multiselect con `$isDisabled` según elegibilidad — los
     * estados no elegibles se muestran deshabilitados (vue-multiselect los
     * respeta y no permite seleccionarlos).
     */
    statusesForSelect() {
      return this.statuses.map(s => {
        const e = this.statusEligibility.get(s.value)
        return { ...s, $isDisabled: !(e && e.eligible) }
      })
    },

    /**
     * Máximo permitido en el input "Metros ejecutados": el tope del estado
     * seleccionado según el modo (ver statusEligibility). Sin estado, el total.
     */
    remainingLength() {
      if (!this.selectedStatus) return this.totalLength
      const e = this.statusEligibility.get(this.selectedStatus.value)
      if (e) return e.cap
      return Math.max(0, this.totalLength - this.existingMetersOfSelectedStatus)
    },

    /** True si la capa tiene rechazos (weight<0) → aplica el modelo de bolsas. */
    hasRejects() {
      return (this.statuses || []).some(s => Number(s.weight) < 0)
    },

    /**
     * Estado de las BOLSAS del segmento (solo capas con rechazos), simulando el
     * historial cronológico como transferencias entre etapas. Ver _SimulatePools.
     */
    pools() {
      return this._SimulatePools()
    },

    /**
     * Desglose para mostrar en el modal: por-iniciar (gris), cada etapa positiva con
     * metros vigentes (su color), y rechazado (color de rechazo, re-ejecutable).
     */
    poolBreakdown() {
      const p = this.pools
      const eps = 1e-6
      const out = []
      if ((p.unstarted || 0) > eps) {
        out.push({ key: 'unstarted', label: this.$t('pending_execution'), color: '#9ca3af', meters: p.unstarted })
      }
      for (let k = 0; k < p.M; k++) {
        if ((p.bags[k] || 0) > eps) {
          const st = this.statuses[p.positives[k]]
          out.push({ key: 'bag' + k, label: st.label, color: st.color, meters: p.bags[k] })
        }
      }
      // Rechazados POR ESTADO (cada estado de rechazo con su propio color/label).
      if (p.rejectedByStatus) {
        for (const [sv, meters] of p.rejectedByStatus) {
          if ((meters || 0) <= eps) continue
          const rej = (this.statuses || []).find(s => s.value === sv)
          out.push({
            key: 'rej-' + sv,
            label: rej ? rej.label : this.$t('rejected'),
            color: (rej && rej.color) || '#e17055',
            meters,
          })
        }
      }
      return out
    },

    /**
     * Estado NETO actual de la máquina de estados (solo capas con rechazos): el
     * positivo alcanzado procesando previousSegments en orden CRONOLÓGICO con la
     * MISMA regla de retroceso que statusEligibility (REJECT_RETURN_MODE). El
     * segmento está en UN estado a la vez, no acumula. Devuelve
     * { value, label, color, meters:totalLength } o null si currentPosLevel = -1
     * (sin avance / reset al inicio → 0). Mantener la lógica de currentPosLevel EN
     * SYNC con statusEligibility.
     */
    currentFlowState() {
      if (!this.hasRejects) return null
      const isReject = (s) => Number(s.weight) < 0

      let lastPositiveIdx = -1
      const prevIdx = []
      for (let i = 0; i < this.statuses.length; i++) {
        prevIdx[i] = lastPositiveIdx
        if (!isReject(this.statuses[i])) lastPositiveIdx = i
      }
      const positives = []
      const posLevelOfIdx = {}
      for (let i = 0; i < this.statuses.length; i++) {
        if (!isReject(this.statuses[i])) { posLevelOfIdx[i] = positives.length; positives.push(i) }
      }

      const valueToIdx = new Map(this.statuses.map((s, i) => [s.value, i]))
      const segs = [...(this.previousSegments || [])].sort((a, b) => (a.createdAt || 0) - (b.createdAt || 0))
      let currentPosLevel = -1
      for (const seg of segs) {
        const i = valueToIdx.get(seg.status)
        if (i === undefined) continue
        if (isReject(this.statuses[i])) {
          currentPosLevel = REJECT_RETURN_MODE === 'reset'
            ? -1
            : Math.max(-1, (posLevelOfIdx[prevIdx[i]] ?? 1) - 2)
        } else {
          currentPosLevel = posLevelOfIdx[i]
        }
      }
      if (currentPosLevel < 0) return null
      const st = this.statuses[positives[currentPosLevel]]
      // Metros NETOS del estado actual (parcial y acumulativo; resetea tras un
      // rechazo), no el largo total — el avance puede ser parcial.
      return { value: st.value, label: st.label, color: st.color, meters: this._NetMetersOfStatus(st.value) }
    },

    /**
     * Acumulado por status — {value, label, color, meters}.
     * Se renderiza como lista debajo de la barra para que el usuario vea
     * cuánto lleva ejecutado por cada estado (ej. "🟡 En curso: 30m, 🟢 Completado: 5m").
     */
    todayDate() {
      return new Date()
    },

    statusBreakdown() {
      const map = new Map()
      for (const seg of this.previousSegments) {
        const key = seg.status
        if (!map.has(key)) {
          map.set(key, {
            value: seg.status,
            label: seg.statusLabel || '',
            color: seg.statusColor || '#9ca3af',
            meters: 0,
          })
        }
        map.get(key).meters += seg.meters || 0
      }
      // Si hay un nuevo delta + status seleccionado, lo sumamos al breakdown
      // (preview del próximo apply).
      if (this.meters > 0 && this.selectedStatus) {
        const key = this.selectedStatus.value
        if (!map.has(key)) {
          map.set(key, {
            value: this.selectedStatus.value,
            label: this.selectedStatus.label,
            color: this.selectedStatus.color,
            meters: 0,
          })
        }
        map.get(key).meters += this.meters
      }
      return [...map.values()]
    },
  },

  watch: {
    /**
     * Cuando cambia el status seleccionado, el "remainingLength" cambia
     * (cada status tiene su propio espacio disponible). Si el valor actual
     * de `meters` excede el nuevo máximo, lo clampeamos al borde.
     */
    selectedStatus() {
      if (this.meters > this.remainingLength) {
        this._SetMeters(this.remainingLength)
      }
    },
  },

  methods: {
    /**
     * Sanea el texto del campo de metros: solo dígitos + un punto + máx 2
     * decimales, y clampa el número a [0, remainingLength]. Mantiene `meters`
     * como Number (fuente de verdad para el resto de la lógica).
     */
    _OnMetersInput(val) {
      let s = String(val == null ? '' : val).replace(',', '.').replace(/[^\d.]/g, '')
      const dot = s.indexOf('.')
      if (dot !== -1) {
        const intPart = s.slice(0, dot)
        const decPart = s.slice(dot + 1).replace(/\./g, '').slice(0, 2)
        s = intPart + '.' + decPart
      }

      let n = parseFloat(s)
      if (!Number.isFinite(n) || n < 0) n = 0
      if (n > this.remainingLength) {
        n = Math.round(this.remainingLength * 100) / 100
        s = this._FmtMeters(n)   // reflejamos el clamp en el texto
      }

      this.metersInput = s
      this.meters = (s === '' || s === '.') ? 0 : n
    },

    /** Al salir del campo, normaliza el texto al valor numérico final. */
    _OnMetersBlur() {
      this._SetMeters(this.meters || 0)
    },

    /** Set programático: fija `meters` (redondeado a 2 dec.) y su espejo de texto. */
    _SetMeters(n) {
      const v = Number.isFinite(n) ? Math.round(n * 100) / 100 : 0
      this.meters = v
      this.metersInput = this._FmtMeters(v)
    },

    /** Formato limpio sin ceros de más: "0", "30", "30.03". */
    _FmtMeters(n) {
      const v = Math.round((Number(n) || 0) * 100) / 100
      return String(v)
    },

    /**
     * Abre el modal con los valores iniciales.
     * @param {Object} opts
     * @param {Number}  opts.totalLength        — longitud total de la polilínea (m)
     * @param {Array}   opts.vertices           — [{x,y}, ...] de la polilínea original
     * @param {String}  opts.direction          — 'l2r' | 'r2l' (default 'l2r')
     * @param {Boolean} opts.directionLocked    — si true, no se puede cambiar la dirección
     * @param {Array}   opts.previousSegments   — segmentos ya registrados (no editables acá).
     *                                            cada uno: { meters, status, statusLabel, statusColor }
     * @param {Array}   opts.statuses           — [{value, label, color}] disponibles
     */
    show(opts = {}) {
      this.totalLength      = opts.totalLength || 0
      this.direction        = opts.direction   || 'l2r'
      this.directionLocked  = !!opts.directionLocked
      this.previousSegments = Array.isArray(opts.previousSegments)
        ? opts.previousSegments
        : []
      this.statuses         = Array.isArray(opts.statuses) ? opts.statuses : []
      this.orderMode        = opts.orderMode || 'free'
      // `meters` siempre arranca en 0: este registro es DELTA, no total.
      // El usuario tipea cuánto se avanzó hoy.
      this._SetMeters(0)
      // El status arranca vacío para que el placeholder "Seleccione un estado"
      // sea visible y obligue al cliente a elegir conscientemente.
      this.selectedStatus   = null
      // Cada apertura del modal arranca con la fecha de hoy.
      this.startDate        = new Date()
      this.errorMessage     = ''
      this.isApplying       = false
      // Clonado superficial — solo necesitamos x,y para el SVG. Lo congelamos
      // en una copia para evitar que mutaciones externas alteren el render.
      this.vertices = (opts.vertices || []).map(v => ({ x: v.x, y: v.y }))
      this.isModalActive = true
    },

    hide() {
      this.isModalActive = false
      this.isApplying = false
      this.errorMessage = ''
    },

    /**
     * Click en uno de los extremos del SVG. Si la dirección está lockada
     * (ya hay avance previo), el click es no-op.
     */
    _OnEndpointClick(dir) {
      if (this.directionLocked) return
      this.direction = dir
    },

    /**
     * Construye los puntos de un triángulo (chevron) centrado en (cx, cy),
     * orientado según `angle` (radianes). El triángulo nace apuntando hacia
     * +X y luego se rota.
     */
    _GetArrowPoints(cx, cy, angle) {
      const cos = Math.cos(angle)
      const sin = Math.sin(angle)
      // Coords locales — triángulo apuntando hacia +X.
      const local = [
        { x:  ARROW_SIZE,        y:  0                  }, // punta
        { x: -ARROW_SIZE * 0.6,  y:  ARROW_SIZE * 0.55  }, // base inferior
        { x: -ARROW_SIZE * 0.6,  y: -ARROW_SIZE * 0.55  }, // base superior
      ]
      return local
        .map(p => {
          const rx = p.x * cos - p.y * sin + cx
          const ry = p.x * sin + p.y * cos + cy
          return `${rx.toFixed(2)},${ry.toFixed(2)}`
        })
        .join(' ')
    },

    /**
     * Helpers para renderizar las mini-barras por status.
     * El `statusBreakdown` ya incluye el delta nuevo (preview); estos
     * separan lo "ya registrado" del "preview" para mostrarlos como
     * dos zonas distintas dentro de la misma mini-barra.
     */
    _IsEditingStatus(statusValue) {
      return this.selectedStatus && this.selectedStatus.value === statusValue
    },

    /** Metros ya registrados (sin contar el delta nuevo) para este status. */
    _ExistingMetersOfStatus(statusValue) {
      let total = 0
      for (const seg of this.previousSegments) {
        if (seg.status === statusValue) total += seg.meters || 0
      }
      return total
    },

    /**
     * Metros NETOS acumulados de un status en capas CON rechazos: se procesan
     * los segmentos en orden cronológico y un RECHAZO (weight<0) rechaza TODO
     * lo ejecutado → resetea el acumulado a 0. A diferencia de
     * _ExistingMetersOfStatus (suma cruda de todo el histórico), esto refleja el
     * ejecutado VIGENTE, que es lo que define cuánto FALTA por registrar
     * (totalLength − neto). El avance positivo es parcial y acumulativo (10 + 20
     * = 30); tras un rechazo vuelve a 0.
     */
    _NetMetersOfStatus(statusValue) {
      const isReject = (v) => {
        const s = this.statuses.find(x => x.value === v)
        return s && Number(s.weight) < 0
      }
      const sorted = [...(this.previousSegments || [])]
        .sort((a, b) => (a.createdAt || 0) - (b.createdAt || 0))
      let net = 0
      for (const seg of sorted) {
        if (isReject(seg.status)) net = 0            // rechaza todo → reset
        else if (seg.status === statusValue) net += seg.meters || 0
      }
      return net
    },

    /**
     * Simula el historial (previousSegments) como transferencias entre BOLSAS y
     * devuelve el estado vigente del segmento. Modelo de flujo parcial con retorno:
     *   - Positivo nivel 0 (Ejecutar): toma de (rechazado primero, luego por-iniciar) → bag[0].
     *   - Positivo nivel k≥1 (Aceptar): toma de bag[k-1] → bag[k].
     *   - Rechazo (emparejado con el accept de nivel k): toma de bag[k-1] → rechazado
     *     (esos metros vuelven a estar disponibles para re-ejecutar; NO resetea el resto).
     * La suma (por-iniciar + Σbags + rechazado) es siempre = totalLength.
     * Devuelve { L, positives, posLevelOfIdx, prevIdx, M, bags, unstarted, rejected }.
     */
    _SimulatePools() {
      const L = this.totalLength || 0
      const statuses = this.statuses || []
      const isReject = (i) => Number(statuses[i].weight) < 0

      const positives = []
      const posLevelOfIdx = {}
      const prevIdx = []
      let lastPos = -1
      for (let i = 0; i < statuses.length; i++) {
        prevIdx[i] = lastPos
        if (!isReject(i)) { posLevelOfIdx[i] = positives.length; positives.push(i); lastPos = i }
      }

      const M = positives.length
      const bags = new Array(M).fill(0)
      let unstarted = L
      let rejected = 0
      // Rechazados POR ESTADO (cada estado de rechazo conserva su propio color/label);
      // `rejected` es el total (= suma de este map). Orden de inserción = FIFO.
      const rejectedByStatus = new Map()
      const consumeRejected = (amount) => {
        let toReduce = amount
        for (const [sv, val] of rejectedByStatus) {
          if (toReduce <= 1e-9) break
          const r = Math.min(val, toReduce)
          rejectedByStatus.set(sv, val - r)
          toReduce -= r
        }
      }

      const valueToIdx = new Map(statuses.map((s, i) => [s.value, i]))
      const segs = [...(this.previousSegments || [])].sort((a, b) => (a.createdAt || 0) - (b.createdAt || 0))
      for (const seg of segs) {
        const i = valueToIdx.get(seg.status)
        if (i === undefined) continue
        let m = Math.max(0, Number(seg.meters) || 0)
        if (m <= 0) continue
        if (!isReject(i)) {
          const k = posLevelOfIdx[i]
          if (k === 0) {
            // Ejecutar: re-ejecuta rechazados primero (FIFO), luego por-iniciar.
            const fromRej = Math.min(m, rejected); rejected -= fromRej; m -= fromRej
            if (fromRej > 0) consumeRejected(fromRej)
            const fromNew = Math.min(m, unstarted); unstarted -= fromNew; m -= fromNew
            bags[0] += (fromRej + fromNew)
          } else {
            const take = Math.min(m, bags[k - 1]); bags[k - 1] -= take; bags[k] += take
          }
        } else {
          // Rechazo: consume de la bolsa del nivel anterior al accept emparejado.
          const acceptIdx = prevIdx[i]
          const kAccept = (acceptIdx >= 0 && posLevelOfIdx[acceptIdx] !== undefined) ? posLevelOfIdx[acceptIdx] : 0
          const inputLevel = kAccept - 1
          if (inputLevel >= 0) {
            const take = Math.min(m, bags[inputLevel]); bags[inputLevel] -= take; rejected += take
            const sv = statuses[i].value
            rejectedByStatus.set(sv, (rejectedByStatus.get(sv) || 0) + take)
          }
        }
      }

      return { L, positives, posLevelOfIdx, prevIdx, M, bags, unstarted, rejected, rejectedByStatus }
    },

    /** % de la barra que ocupan `meters` sobre el total (clamp 0..100). */
    _PoolPercent(meters) {
      if (this.totalLength <= 0) return 0
      return Math.max(0, Math.min((Number(meters) || 0) / this.totalLength * 100, 100))
    },

    /** % del total que cubre lo ya registrado del status. */
    _ExistingPercentOfStatus(statusValue) {
      if (this.totalLength <= 0) return 0
      return Math.max(0, Math.min(this._ExistingMetersOfStatus(statusValue) / this.totalLength * 100, 100))
    },

    /** % del total que cubre el delta nuevo del status editado (preview). */
    _PreviewPercentOfStatus(statusValue) {
      if (this.totalLength <= 0 || !this._IsEditingStatus(statusValue)) return 0
      const existing = this._ExistingMetersOfStatus(statusValue)
      const available = Math.max(0, this.totalLength - existing)
      const preview = Math.min(this.meters || 0, available)
      return Math.max(0, Math.min(preview / this.totalLength * 100, 100))
    },

    _OnApply() {
      if (!this.selectedStatus) {
        this.$buefy?.toast?.open?.({
          message:  this.$t('select_status'),
          type:     'is-warning',
          position: 'is-top-right',
        })
        return
      }
      // Estado ya completo (sin margen): mensaje claro en vez de "introduzca
      // avance". Cubre el caso de tipear un valor > lo permitido (se clampa a 0).
      if (this.remainingLength <= 1e-6) {
        this.$buefy?.toast?.open?.({
          message:  this.$t('status_already_complete'),
          type:     'is-warning',
          position: 'is-top-right',
        })
        return
      }
      if (!this.meters || this.meters <= 0) {
        this.$buefy?.toast?.open?.({
          message:  this.$t('enter_executed_meters'),
          type:     'is-warning',
          position: 'is-top-right',
        })
        return
      }
      // Encendemos loading y emitimos. El padre va a persistir en backend
      // y llamar a `hide()` (éxito) o `setError(msg)` (fallo).
      this.errorMessage = ''
      this.isApplying = true
      this.$emit('epc-apply', {
        direction:   this.direction,
        meters:      this.meters,
        status:      this.selectedStatus.value,
        statusLabel: this.selectedStatus.label,
        statusColor: this.selectedStatus.color,
        startDate:   this._FormatYMD(this.startDate),
      })
    },

    /** Date → 'YYYY-MM-DD' para el payload del backend. */
    _FormatYMD(date) {
      const d = date instanceof Date ? date : new Date(date)
      const yyyy = d.getFullYear()
      const mm   = String(d.getMonth() + 1).padStart(2, '0')
      const dd   = String(d.getDate()).padStart(2, '0')
      return `${yyyy}-${mm}-${dd}`
    },

    /**
     * Llamado por el padre cuando la persistencia falla. Quita el loading
     * y muestra el error inline para que el usuario pueda reintentar
     * sin volver a tipear todo.
     */
    setError(message) {
      this.isApplying = false
      this.errorMessage = message || ''
    },
  },
}
</script>

<style scoped>
.advance-svg-wrap {
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  padding: 4px;
  display: flex;
  justify-content: center;
}

.advance-svg {
  display: block;
}

.endpoint-grp {
  cursor: pointer;
}

.endpoint-grp--locked {
  cursor: not-allowed;
}

.endpoint {
  fill: #d1d5db;
  stroke: #fff;
  stroke-width: 2;
  transition: fill 0.15s ease, stroke 0.15s ease;
}

.endpoint-grp:not(.endpoint-grp--locked):hover .endpoint {
  fill: #9ca3af;
}

.endpoint--selected {
  fill: #5577BB;   /* primary de la app */
  stroke: #fff;
}

.endpoint-grp:not(.endpoint-grp--locked):hover .endpoint--selected {
  fill: #385DA5;   /* primary-dark al hover */
}

/* Cuando está lockado, suavizamos el extremo no-seleccionado para
   reforzar visualmente "solo lectura". */
.endpoint-grp--locked .endpoint:not(.endpoint--selected) {
  opacity: 0.5;
}

.endpoint-label {
  fill: #fff;
  font-size: 11px;
  font-weight: 700;
  user-select: none;
  pointer-events: none;
}

.advance-arrow {
  fill: #5577BB;   /* mismo primary que el endpoint seleccionado */
  pointer-events: none;
}

/* Resumen del avance — info de "ya registrado" + barra de progreso visual. */
.execution-summary {
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  padding: 8px 12px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  font-size: 13px;
}

.execution-summary__row {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.execution-summary__row--small {
  font-size: 11px;
  color: #6b7280;
}

.execution-summary__label {
  font-size: 11px;
  color: #6b7280;
  text-transform: uppercase;
  font-weight: 600;
  letter-spacing: 0.03em;
}

.execution-summary__bar-wrap {
  margin-top: 2px;
}

.status-breakdown {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-top: 4px;
}

.status-breakdown__row {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  padding: 2px 4px;
  border-radius: 4px;
  transition: background-color 0.15s ease;
}

/* Resalte sutil para el status que el usuario está editando. */
.status-breakdown__row--editing {
  background-color: rgba(85, 119, 187, 0.08);
}

.status-breakdown__label {
  color: #374151;
  font-weight: 500;
  min-width: 90px;
  max-width: 130px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Mini-barra de progreso por status — % del status sobre el total. */
.status-breakdown__bar {
  position: relative;
  flex: 1;
  height: 8px;
  background: #e5e7eb;
  border-radius: 4px;
  overflow: hidden;
}

.status-breakdown__bar-fill {
  position: absolute;
  left: 0;
  top: 0;
  height: 100%;
  transition: width 0.2s ease;
}

/* Preview del nuevo delta: se renderiza al final del fill existente con
   rayas diagonales para distinguirlo de lo ya registrado. */
.status-breakdown__bar-preview {
  position: absolute;
  top: 0;
  height: 100%;
  background-image: repeating-linear-gradient(
    45deg,
    rgba(255, 255, 255, 0.55) 0,
    rgba(255, 255, 255, 0.55) 2px,
    transparent 2px,
    transparent 5px
  );
  background-blend-mode: overlay;
  transition: width 0.2s ease, left 0.2s ease;
}

.status-breakdown__meters {
  font-variant-numeric: tabular-nums;
  font-weight: 600;
  color: #1f2937;
  min-width: 70px;
  text-align: right;
}

/* Swatch de color del status — renderizado en los slots option/singleLabel
   del multiselect. */
.status-swatch {
  display: inline-block;
  width: 16px;
  height: 16px;
  border-radius: 4px;
  border: 1px solid rgba(0, 0, 0, 0.1);
  flex-shrink: 0;
}

/* Override mínimo del multiselect para alinearlo visualmente con los
   otros campos del modal (b-numberinput / b-radio-button). */
.advance-status-select :deep(.multiselect__tags) {
  min-height: 38px;
  padding: 6px 40px 0 8px;
  font-size: 14px;
}

.advance-status-select :deep(.multiselect__placeholder) {
  color: #9ca3af;
  padding-top: 4px;
}
</style>

<!-- CSS de vue-multiselect — fuera del scope para que aplique al dropdown
     (que se renderiza como portal y no respeta los estilos scoped). -->
<style src="vue-multiselect/dist/vue-multiselect.min.css"></style>
